import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled, { keyframes } from 'styled-components';
import { useState, useRef, useEffect, forwardRef, createElement } from 'react';
import { useClickOutside, formatDate, useFuse } from '@eir/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket, faAt, faCalendarDay, faCalendarDays, faCalendarPlus, faCalendarXmark, faCaretDown, faCheck, faChevronDown, faCircleHalfStroke, faCircleNotch, faCirclePlus, faClockRotateLeft, faCog, faEarthEurope, faEllipsis, faEllipsisH, faExclamationCircle, faExclamationTriangle, faFilePdf, faInfoCircle, faList, faLocationCrosshairs, faLocationDot, faMapLocationDot, faPencil, faPhone, faPlus, faQuestion, faRotateLeft, faRotateRight, faRoute, faSearch, faSliders, faSortAlphaDown, faSortAlphaDownAlt, faSortDown, faSortUp, faTimes, faTrashAlt, faUpRightFromSquare, faUser, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle, faCircle, faClone, faCopy, faDotCircle, faFileLines, faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { useNotification } from '@eir/notifications';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const Button$1 = ({ children, type = 'button', variant = 'primary', theme = 'orange', size = 'medium', onClick, disabled = false, }) => {
    const colors = {
        primary: {
            blue: {
                fontColor: '#fff',
                backgroundColor: '#3a58de',
                border: '0.0625rem solid #1e38ae',
                hover: {
                    backgroundColor: '#2343d1',
                    borderColor: '#1e38ae',
                    boxShadow: 'rgba(30, 56, 174, 0.3) 0 1px 3px 1px',
                },
            },
            green: {
                fontColor: '#fff',
                backgroundColor: '#42c67f',
                border: '0.0625rem solid #2a8d58',
                hover: {
                    backgroundColor: '#34ad6c',
                    borderColor: '#2a8d58',
                    boxShadow: 'rgba(42, 141, 88, 0.3) 0 1px 3px 1px',
                },
            },
            grey: {
                fontColor: '#24292f',
                backgroundColor: '#f5f5f5',
                border: '0.0625rem solid #e6e6e6',
                hover: {
                    backgroundColor: '#e0e0e0',
                    borderColor: '#e6e6e6',
                    boxShadow: 'rgba(230, 230, 230, 0.3) 0 1px 3px 1px',
                },
            },
            orange: {
                fontColor: '#fff',
                backgroundColor: '#fc8e49',
                border: '0.0625rem solid #f15f04',
                hover: {
                    backgroundColor: '#fc7522',
                    borderColor: '#f15f04',
                    boxShadow: 'rgba(241, 83, 3, 0.3) 0 1px 3px 1px',
                },
            },
            red: {
                fontColor: '#fff',
                backgroundColor: '#ff6f6f',
                border: '0.0625rem solid #ff1f1f',
                hover: {
                    backgroundColor: '#ff4747',
                    borderColor: '#ff1f1f',
                    boxShadow: 'rgba(255, 89, 31, 0.3) 0 1px 3px 1px',
                },
            },
        },
        secondary: {
            blue: {
                fontColor: '#3a58de',
                backgroundColor: 'transparent',
                border: '0.0625rem solid #3a58de',
                hover: {
                    fontColor: '#2343d1',
                    backgroundColor: 'transparent',
                    borderColor: '#2343d1',
                    boxShadow: 'rgba(30, 56, 174, 0.3) 0 1px 3px 1px',
                },
            },
            green: {
                fontColor: '#42c67f',
                backgroundColor: 'transparent',
                border: '0.0625rem solid #42c67f',
                hover: {
                    fontColor: '#34ad6c',
                    backgroundColor: 'transparent',
                    borderColor: '#34ad6c',
                    boxShadow: 'rgba(42, 141, 88, 0.3) 0 1px 3px 1px',
                },
            },
            orange: {
                fontColor: '#fc8e49',
                backgroundColor: 'transparent',
                border: '0.0625rem solid #fc8e49',
                hover: {
                    fontColor: '#fc7522',
                    backgroundColor: 'transparent',
                    borderColor: '#fc7522',
                    boxShadow: 'rgba(241, 83, 3, 0.3) 0 1px 3px 1px',
                },
            },
            red: {
                fontColor: '#ff6f6f',
                backgroundColor: 'transparent',
                border: '0.0625rem solid #ff6f6f',
                hover: {
                    fontColor: '#ff4747',
                    backgroundColor: 'transparent',
                    borderColor: '#ff4747',
                    boxShadow: 'rgba(255, 89, 31, 0.3) 0 1px 3px 1px',
                },
            },
        },
    };
    const sizes = {
        medium: {
            padding: '0.5rem 0.75rem',
            fontSize: '1rem',
        },
        small: {
            padding: '0.25rem 0.5rem',
            fontSize: '0.75rem',
        },
    };
    const handleClick = (event) => {
        onClick && onClick(event);
    };
    return (jsx(StyledButton, Object.assign({ type: type, themeColors: colors[variant][theme], size: sizes[size], disabled: disabled, onClick: handleClick }, { children: children })));
};
const StyledButton = styled.button `
  border-radius: 0.25rem;
  line-height: 1;
  border: none;
  font-size: ${({ size }) => size.fontSize};
  color: ${({ themeColors }) => themeColors.fontColor};
  background-color: ${({ themeColors }) => themeColors.backgroundColor};
  border: ${({ themeColors }) => themeColors.border};
  padding: ${({ size }) => size.padding};
  cursor: pointer;
  transition: all 0.3s ease-in-out 0s;

  &:hover {
    color: ${({ themeColors }) => themeColors.hover.fontColor};
    background-color: ${({ themeColors }) => themeColors.hover.backgroundColor};
    border-color: ${({ themeColors }) => themeColors.hover.borderColor};
    box-shadow: ${({ themeColors }) => themeColors.hover.boxShadow};
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const ButtonsWrapper = styled.div `
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const Icon = ({ iconName, color = 'inherit', size = '1x' }) => {
    const icons = {
        faArrowRightFromBracket,
        faAt,
        faCalendarDay,
        faCalendarDays,
        faCalendarPlus,
        faCalendarXmark,
        faCaretDown,
        faCheck,
        faCheckCircle,
        faChevronDown,
        faCircle,
        faCircleHalfStroke,
        faCircleNotch,
        faCirclePlus,
        faClone,
        faClockRotateLeft,
        faCog,
        faCopy,
        faDotCircle,
        faEarthEurope,
        faEllipsis,
        faEllipsisH,
        faExclamationCircle,
        faExclamationTriangle,
        faFileLines,
        faFilePdf,
        faInfoCircle,
        faList,
        faLocationCrosshairs,
        faLocationDot,
        faMapLocationDot,
        faPencil,
        faPhone,
        faPlus,
        faQuestion,
        faRotateLeft,
        faRotateRight,
        faRoute,
        faSearch,
        faSliders,
        faSortAlphaDown,
        faSortAlphaDownAlt,
        faSortDown,
        faSortUp,
        faTimes,
        faTimesCircle,
        faTrashAlt,
        faUpRightFromSquare,
        faUser,
        faXmark,
    };
    return jsx(FontAwesomeIcon, { color: color, icon: icons[iconName], size: size });
};

const IconButton = ({ children, options = { size: 'medium' }, tooltipText, disabled = false, onClick, }) => {
    const handleClick = (event) => {
        onClick && onClick(event);
    };
    const sizes = {
        small: {
            height: '1.2rem',
            width: '1.2rem',
        },
        medium: {
            height: '1.5rem',
            width: '1.5rem',
        },
        large: {
            height: '1.8rem',
            width: '1.8rem',
        },
        xlarge: {
            height: '2.2rem',
            width: '2.2rem',
        },
    };
    return (jsx(Button, Object.assign({}, options, { size: sizes[options.size], disabled: disabled, onClick: handleClick }, { children: children })));
};
const Button = styled.button `
  height: ${({ size }) => size.height};
  width: ${({ size }) => size.width};
  padding: 0;
  border: 0;
  outline: 0;
  background-color: transparent;
  cursor: pointer;
  position: relative;
  color: ${({ color }) => (color ? color : 'inherit')};

  &:enabled {
    &:before {
      content: '';
      position: absolute;
      display: block;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: #202124;
      border: 1px solid #000;
      border-radius: 50%;
      transform: scale(0);
      opacity: 0;
      transition: all 0.15s;
    }

    &:hover {
      color: ${({ hoverColor }) => (hoverColor ? hoverColor : '#fc8e49')};

      &:before {
        transform: scale(1);
        opacity: 0.06;
      }
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Section = styled.section `
  border: ${({ showBorder }) => showBorder ? '0.125rem solid #ebebeb' : 'none'};
  border-radius: 0.5rem;

  header {
    background-color: #ebebeb;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 0.5rem 1rem;

    h3 {
      line-height: 1;
    }
  }

  main {
    background-color: #fff;
    border-radius: 0 0 0.5rem 0.5rem;
    padding: 0.5rem;
    margin-left: 0;
  }
`;

const ContextMenuItem = ({ option, hideMenu }) => {
    const colors = {
        blue: {
            fontColor: '#3a58de',
            hover: {
                fontColor: '#2343d1',
                backgroundColor: '#EEF0FC',
            },
        },
        green: {
            fontColor: '#42c67f',
            hover: {
                fontColor: '#34ad6c',
                backgroundColor: '#effaf4',
            },
        },
        red: {
            fontColor: '#ff6f6f',
            hover: {
                fontColor: '#ff4747',
                backgroundColor: '#ffebeb',
            },
        },
    };
    const handleClick = () => {
        hideMenu();
        option.action();
    };
    return (jsx("li", { children: jsx(MenuButton, Object.assign({ onClick: handleClick, themeColors: colors[option.color] }, { children: option.text })) }));
};
const MenuButton = styled.button.attrs({ type: 'button' }) `
  text-align: left;
  padding: 0.5rem 0.5rem;
  padding-right: 2rem;
  font-size: 1rem;
  border: none;
  width: 100%;
  white-space: nowrap;
  color: ${({ themeColors }) => themeColors.fontColor};
  background-color: transparent;
  border-radius: 0.25rem;
  cursor: pointer;

  &:hover {
    color: ${({ themeColors }) => themeColors.hover.fontColor};
    background-color: ${({ themeColors }) => themeColors.hover.backgroundColor};
  }
`;

const ContextMenuContents = ({ options, hideMenu, }) => {
    return (jsx(MenuSection, { children: jsx("ul", { children: options.map((option, index) => (jsx(ContextMenuItem, { option: option, hideMenu: hideMenu }, index))) }) }));
};
const MenuSection = styled(Section) `
  position: absolute;
  right: 0;
  background-color: #fff;
  padding: 0.5rem 0.25rem;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 0.5rem 1rem;
  border: 0.0625rem solid #e6e6e6;
  z-index: 10;

  ul {
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }
`;

const ContextMenu = ({ options }) => {
    const [showMenu, setShowMenu] = useState(false);
    const ref = useRef(null);
    const toggleMenu = () => setShowMenu(!showMenu);
    const hideMenu = () => setShowMenu(false);
    useClickOutside(ref, hideMenu);
    return (jsxs(MenuWrapper, Object.assign({ ref: ref }, { children: [jsx(IconButton, Object.assign({ options: { size: 'medium' }, onClick: toggleMenu }, { children: jsx(Icon, { iconName: 'faEllipsis' }) })), showMenu && (jsx(ContextMenuContents, { options: options, hideMenu: hideMenu }))] })));
};
const MenuWrapper = styled.div `
  position: relative;
  margin: auto;
`;

const CopyText = ({ text }) => {
    const { addNotification, removeNotificationWithDelay } = useNotification();
    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        removeNotificationWithDelay(addNotification(`"${text}" was copied to clipboard`, 'success'));
    };
    return (jsxs(CopyTextWrapper, { children: [jsx("input", { type: 'text', defaultValue: text }), jsx("button", Object.assign({ type: 'button', onClick: copyToClipboard }, { children: jsx(Icon, { iconName: 'faClone', color: '#1e38ae' }) }))] }));
};
const CopyTextWrapper = styled.div `
  display: flex;
  gap: 0.25rem;
  font-size: 1rem;

  button {
    cursor: pointer;
    border: none;
    background-color: transparent;
    padding: 0;
  }

  input {
    flex: 1;
    line-height: 1.3;
    border: none;
    padding: 0;
  }
`;

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2;
var HorizontalSelectRow = function HorizontalSelectRow(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/jsx(Ul, {
    children: children.map(function (child) {
      return /*#__PURE__*/jsx(Li, {
        children: child
      }, child.key);
    })
  });
};
HorizontalSelectRow.propTypes = {
  children: PropTypes.node.isRequired
};
var Ul = styled.ul(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  margin: 0.25rem 0;\n  list-style: none;\n  display: flex;\n"])));
var Li = styled.li(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  input {\n    opacity: 0;\n    width: 0;\n    & + label {\n      font-size: 1rem;\n      font-weight: 300;\n      padding: 0.0625rem 0.5rem;\n      margin-right: 0.25rem;\n      border: 0.0625rem solid #7b7d8c;\n      border-radius: 0.125rem;\n      background-color: #f8f8f8;\n      cursor: pointer;\n      transition: 0.2s ease-out;\n\n      &:hover {\n        color: #fc7522;\n        border-color: #fc7522;\n        box-shadow: rgba(241, 83, 4, 0.3) 0 1px 3px 1px;\n      }\n    }\n\n    &:checked + label {\n      color: #fc8e49;\n      font-weight: 400;\n      border-color: #fc8e49;\n      border-width: 0.125rem;\n      padding: 0 0.4375rem;\n    }\n  }\n"])));

const NumericInput = ({ name, value, onChange, onBlur, disabled = false, autoFocus = false, }) => {
    const [isEditing, setIsEditing] = useState(false);
    const inputRef = useRef(null);
    const handleFocus = () => setIsEditing(true);
    const handleBlur = (event) => {
        setIsEditing(false);
        onBlur && onBlur(event);
    };
    const handleChange = (event) => onChange(event);
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.select();
        }
    }, [isEditing]);
    return isEditing ? (jsx("input", { ref: inputRef, type: 'number', name: name, autoComplete: 'off', value: value, onChange: handleChange, onBlur: handleBlur })) : (jsx("input", { ref: inputRef, type: 'text', name: name, autoComplete: 'off', value: Number(value).toLocaleString('sv-SE'), readOnly: true, disabled: disabled, autoFocus: autoFocus, onFocus: handleFocus, style: { textAlign: 'right' } }));
};

const UpdateDate = forwardRef(({ value, onClick }, ref) => {
    return (jsxs(Fragment, { children: [value ? value : 'Not selected', jsx(IconButton, Object.assign({ options: { size: 'small' }, onClick: onClick }, { children: jsx(Icon, { iconName: 'faCalendarDays' }) }))] }));
});

const EditContent = ({ initialValue, onUpdate, hideEdit, type, emailListVariables, }) => {
    const [value, setValue] = useState(initialValue);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [inputMaxWidth, setInputMaxWidth] = useState(0);
    const [showEmailVariables, setShowEmailVariables] = useState(false);
    const editContainerRef = useRef(null);
    const { setExecuteCallback } = useClickOutside(editContainerRef, hideEdit);
    const inputRef = useRef(null);
    const spanRef = useRef(null);
    const doUpdate = (newValue) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (onUpdate) {
            try {
                yield onUpdate(newValue instanceof Date ? formatDate(newValue) : newValue.trim());
                hideEdit();
            }
            catch (error) {
                if (inputRef) {
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.setSelectionRange(cursorPosition, cursorPosition);
                }
            }
        }
    });
    const handleUpdateClick = () => doUpdate(value);
    useEffect(() => {
        if (inputRef.current) {
            setInputMaxWidth(window.innerWidth - inputRef.current.getBoundingClientRect().left);
        }
    }, [inputRef]);
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.removeProperty('height');
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    }, [value]);
    useEffect(() => {
        setExecuteCallback(true);
    }, [value]);
    useEffect(() => {
        if (spanRef.current && inputRef.current) {
            spanRef.current.innerHTML = value;
            inputRef.current.style.width = `${spanRef.current.offsetWidth}px`;
        }
    }, [value, inputRef, spanRef]);
    useEffect(() => {
        !showEmailVariables && setExecuteCallback(true);
    }, [showEmailVariables]);
    const handleCancelClick = () => hideEdit();
    const handleChange = (event) => setValue(event.target.value);
    const handleBlur = (event) => setCursorPosition(event.target.selectionStart || 0);
    const handleChangeDate = (date, event) => {
        setExecuteCallback(false);
        setValue(formatDate(date));
    };
    const getInput = (type) => {
        switch (type) {
            case 'date':
                return (jsx(DatePicker, { className: 'default', locale: 'sv', dateFormat: 'yyyy-MM-dd', selected: value ? new Date(value) : null, onChange: handleChangeDate, customInput: createElement(UpdateDate) }));
            case 'number':
                return jsx(NumericInput, { value: value, autoFocus: true, onChange: handleChange });
            case 'yesNo':
                return (jsxs(HorizontalSelectRow, { children: [jsxs("div", { children: [jsx("input", { type: 'radio', name: 'yesNoSelector', id: 'yes-no-selector-yes', value: 'Yes', onChange: handleChange, checked: value === 'Yes' }), jsx("label", Object.assign({ htmlFor: 'yes-no-selector-yes' }, { children: "Yes" }))] }), jsxs("div", { children: [jsx("input", { type: 'radio', name: 'yesNoSelector', id: 'yes-no-selector-no', value: 'No', onChange: handleChange, checked: value === 'No' }), jsx("label", Object.assign({ htmlFor: 'yes-no-selector-no' }, { children: "No" }))] })] }));
            case 'text':
                return (jsx(Textarea, { ref: inputRef, value: value, autoFocus: true, maxWidth: inputMaxWidth, onChange: handleChange, onBlur: handleBlur }));
        }
    };
    return (jsx(EditContainer, Object.assign({ ref: editContainerRef }, { children: jsxs(FormWrapper, { children: [getInput(type), jsxs(ButtonContainer, { children: [jsx(Button$1, Object.assign({ variant: 'secondary', size: 'small', onClick: handleCancelClick }, { children: "Cancel" })), jsx(Button$1, Object.assign({ size: 'small', onClick: handleUpdateClick }, { children: "Update" }))] })] }) })));
};
const EditContainer = styled.div `
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 0.5rem;
  position: absolute;
  top: 0;
  z-index: 10;
  box-shadow: 0 0.2rem 0.4rem 0 rgba(0, 0, 0, 0.3);
`;
const ButtonContainer = styled.div `
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;
const FormWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const Textarea = styled.textarea `
  max-width: 20rem;
  min-width: 15rem;
  height: 1.625rem;
  font-size: 0.8rem;
  padding: 0.125rem 0.25rem;
`;
styled.div `
  position: relative;
  display: flex;
`;

const ValueSelector = ({ data, ListItemTemplate, onSelect, hideSelector, filterSettings, }) => {
    const [listMaxHeight, setListMaxHeight] = useState(0);
    const [showListBelow, setShowListBelow] = useState(true);
    const selectorRef = useRef(null);
    const listRef = useRef(null);
    const { filteredList, pattern, handlePatternChange } = useFuse(data, filterSettings.options);
    useClickOutside(selectorRef, hideSelector);
    const handleSelectClick = (value) => () => {
        onSelect && onSelect(value);
        hideSelector();
    };
    useEffect(() => {
        if (listRef.current) {
            const maxHeightShowListBelow = window.innerHeight - listRef.current.getBoundingClientRect().top;
            const maxHeightShowListAbove = window.innerHeight - maxHeightShowListBelow;
            if (maxHeightShowListBelow - 16 < 150 &&
                maxHeightShowListBelow < maxHeightShowListAbove - 16) {
                setShowListBelow(false);
                setListMaxHeight(maxHeightShowListAbove);
            }
            else {
                setShowListBelow(true);
                setListMaxHeight(maxHeightShowListBelow);
            }
        }
    }, [listRef]);
    return (jsxs(SelectorWrapper, Object.assign({ ref: selectorRef, maxHeight: listMaxHeight, showBelow: showListBelow }, { children: [filterSettings.showFilter && (jsx(FilterWrapper, { children: jsx("input", { type: 'text', placeholder: 'Filter items', value: pattern, onChange: handlePatternChange, autoFocus: true }) })), jsx("ul", Object.assign({ ref: listRef }, { children: filteredList.map((item, index) => (jsx("li", Object.assign({ onClick: handleSelectClick(item.value) }, { children: jsx(ListItemTemplate, Object.assign({}, item)) }), index))) }))] })));
};
const SelectorWrapper = styled.div `
  min-width: 10rem;
  background-color: #fff;
  position: absolute;
  ${({ showBelow }) => (showBelow ? 'top: 0' : 'bottom: 0')};
  z-index: 100;
  border-radius: 0.5rem;
  box-shadow: 0 0.2rem 0.4rem 0 rgba(0, 0, 0, 0.3);

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    max-height: calc(${({ maxHeight }) => maxHeight}px - 1rem);
    overflow-y: auto;

    li {
      border-bottom: 0.0625rem solid #dedede;
      padding: 0.5rem 0;
      cursor: pointer;

      &:hover {
        color: #fc8e49;
        text-decoration: underline;
      }
    }
  }
`;
const FilterWrapper = styled.div `
  display: flex;
  background-color: #fff;
  padding: 0.25rem;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;

  input {
    flex: 1;
  }
`;

const EditableContent = ({ value, text, type, listData, ListItemTemplate, onDelete, onUpdate, onSelect, filterSettings, emailListVariables, }) => {
    const [showEditField, setShowEditField] = useState(false);
    const handleEditClick = (event) => {
        event.stopPropagation();
        setShowEditField(true);
    };
    const handleDeleteClick = () => {
        onDelete && onDelete();
    };
    const hideEdit = () => setShowEditField(false);
    return (jsxs(EditWrapper, { children: [text
                ? text
                : type === 'number'
                    ? value
                        ? Number(value).toLocaleString('sv-SE')
                        : 'Not set'
                    : value, showEditField ? (type === 'list' ? (ListItemTemplate &&
                filterSettings &&
                onSelect && (jsx(ValueSelector, { data: listData, ListItemTemplate: ListItemTemplate, onSelect: onSelect, hideSelector: hideEdit, filterSettings: filterSettings }))) : (onUpdate && (jsx(EditContent, { type: type, initialValue: value ? value.toString() : '', onUpdate: onUpdate, hideEdit: hideEdit, emailListVariables: emailListVariables })))) : (jsxs(Fragment, { children: [jsx(IconButton, Object.assign({ options: { size: 'medium' }, onClick: handleEditClick }, { children: jsx(Icon, { iconName: 'faPencil' }) })), value && onDelete && (jsx(IconButton, Object.assign({ options: { size: 'medium' }, onClick: handleDeleteClick }, { children: jsx(Icon, { iconName: 'faTrashAlt' }) })))] }))] }));
};
const EditWrapper = styled.div `
  position: relative;
`;

const GearSpinner = ({ waitingMessage }) => {
    return (jsxs(LoadingContainer, { children: [jsxs(GearsWrapper, { children: [jsx(BigGear, { children: jsx(Icon, { iconName: 'faCog' }) }), jsx(SmallGear, { children: jsx(Icon, { iconName: 'faCog' }) })] }), waitingMessage && jsx("h2", { children: waitingMessage.trim() })] }));
};
const rotation = keyframes `
    from {transform: rotate(0deg);}
    to {transform: rotate(360deg);}
`;
const LoadingContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const GearsWrapper = styled.div `
  display: flex;
  line-height: 1;

  svg {
    animation-name: ${rotation};
    animation-timing-function: linear;
    animation-iteration-count: infinite;
  }
`;
const BigGear = styled.div `
  font-size: 10rem;
  animation-duration: 3s;
  margin-bottom: 1rem;

  svg {
    animation-duration: 3s;
  }
`;
const SmallGear = styled.div `
  color: var(--eir-partner-primary-color);
  font-size: 6rem;
  margin-left: -0.25rem;
  align-self: flex-end;

  svg {
    animation-duration: 2s;
    animation-direction: reverse;
  }
`;

const GridTableCell = styled.div `
  padding: 0.375rem 0.5rem;
  border-bottom: 0.0625rem dotted #dedede; ;
`;

const GridTable = ({ data, columns, Row, tableRowCustomProps, Footer, footerData, sort, isLoading = false, noOfLoadingRows = 15, }) => {
    const [sortOptions, setSortOptions] = useState(sort !== null && sort !== void 0 ? sort : { columnName: '', direction: 'asc' });
    const sortData = (a, b) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        if (!sort)
            return 1;
        switch ((_a = columns.find(column => column.name === sortOptions.columnName)) === null || _a === void 0 ? void 0 : _a.type) {
            case 'boolean':
                return a[sortOptions.columnName] === b[sortOptions.columnName]
                    ? 0
                    : sortOptions.direction === 'asc'
                        ? b[sortOptions.columnName]
                            ? 1
                            : -1
                        : b[sortOptions.columnName]
                            ? -1
                            : 1;
            case 'number':
                return ((_b = a[sortOptions.columnName]) !== null && _b !== void 0 ? _b : 0) >
                    ((_c = b[sortOptions.columnName]) !== null && _c !== void 0 ? _c : 0)
                    ? sortOptions.direction === 'asc'
                        ? 1
                        : -1
                    : ((_d = b[sortOptions.columnName]) !== null && _d !== void 0 ? _d : 0) > ((_e = a[sortOptions.columnName]) !== null && _e !== void 0 ? _e : 0)
                        ? sortOptions.direction === 'asc'
                            ? -1
                            : 1
                        : 0;
            default:
                return ((_g = (_f = a[sortOptions.columnName]) === null || _f === void 0 ? void 0 : _f.toLowerCase()) !== null && _g !== void 0 ? _g : '') ===
                    ((_j = (_h = b[sortOptions.columnName]) === null || _h === void 0 ? void 0 : _h.toLowerCase()) !== null && _j !== void 0 ? _j : '')
                    ? 0
                    : ((_l = (_k = a[sortOptions.columnName]) === null || _k === void 0 ? void 0 : _k.toLowerCase()) !== null && _l !== void 0 ? _l : '') >
                        ((_o = (_m = b[sortOptions.columnName]) === null || _m === void 0 ? void 0 : _m.toLowerCase()) !== null && _o !== void 0 ? _o : '')
                        ? sortOptions.direction === 'asc'
                            ? 1
                            : -1
                        : sortOptions.direction === 'asc'
                            ? -1
                            : 1;
        }
    };
    const handleSortClick = (columnName) => () => {
        var _a;
        return ((_a = columns.find(column => column.name === columnName)) === null || _a === void 0 ? void 0 : _a.isSortable) === true &&
            (columnName === (sortOptions === null || sortOptions === void 0 ? void 0 : sortOptions.columnName)
                ? setSortOptions((state) => (Object.assign(Object.assign({}, state), { direction: sortOptions.direction === 'asc' ? 'desc' : 'asc' })))
                : setSortOptions((state) => (Object.assign(Object.assign({}, state), { columnName }))));
    };
    return (jsxs(Table, Object.assign({ columnsWidth: columns.map(() => 'auto').join(' ') }, { children: [columns.map(column => {
                var _a;
                return (jsxs(GridTableHeaderCell, Object.assign({ textAlign: (_a = column.textAlign) !== null && _a !== void 0 ? _a : 'left', onClick: handleSortClick(column.name), isSortable: column.isSortable }, { children: [column.header, column.name === sortOptions.columnName &&
                            (sortOptions.direction === 'asc' ? (jsxs(Fragment, { children: [' ', jsx(Icon, { iconName: 'faSortUp' })] })) : (jsxs(Fragment, { children: [' ', jsx(Icon, { iconName: 'faSortDown' })] })))] }), column.name));
            }), isLoading
                ? [...Array(columns.length * noOfLoadingRows)].map((x, i) => (jsx(GridTableCell, { children: jsx(Skeleton, {}) }, i)))
                : data
                    .sort(sortData)
                    .map((content, index) => (jsx(Row, Object.assign({ content: content }, tableRowCustomProps), index))), Footer && Footer] })));
};
const Table = styled.div `
  display: grid;
  grid-template-columns: ${({ columnsWidth }) => columnsWidth};
  background-color: #fff;
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-size: 0.875rem;
`;
const GridTableHeaderCell = styled(GridTableCell) `
  border-bottom: 0.125rem solid #dedede;
  font-weight: 500;
  text-align: ${({ textAlign }) => textAlign};
  cursor: ${({ isSortable }) => (isSortable ? 'pointer' : 'inherit')};
  text-align: ${({ textAlign }) => (textAlign ? textAlign : 'left')};
`;

const ListItem = forwardRef(({ children, theme = 'grey', type = 'default', isNew = false, onClick, newItemRef, }) => {
    const itemRef = useRef(null);
    useEffect(() => {
        if (isNew && itemRef.current) {
            itemRef.current.scrollIntoView();
        }
    }, [itemRef.current]);
    const colors = {
        green: {
            fontColor: '#3c763d',
            backgroundColor: '#dff0d8',
            borderColor: '#3c763d',
        },
        grey: {
            fontColor: '#24292f',
            backgroundColor: '#fafafa',
            borderColor: '#e6e6e6',
        },
        lightOrange: {
            fontColor: '#24292f',
            backgroundColor: '#FEE6D7',
            borderColor: '#fda772',
        },
        red: {
            fontColor: '#721c24',
            backgroundColor: '#f8d7da',
            borderColor: '#721c24',
        },
        yellow: {
            fontColor: '#856404',
            backgroundColor: '#fff3cd',
            borderColor: '#856404',
        },
    };
    const handleClick = () => {
        onClick && onClick();
    };
    const items = {
        addNew: (jsx(NewItem, Object.assign({ ref: newItemRef, themeColors: colors[theme] }, { children: children }))),
        default: jsx(Item, Object.assign({ themeColors: colors[theme] }, { children: children })),
        selectable: (jsx(SelectableItem, Object.assign({ themeColors: colors[theme], onClick: handleClick }, { children: children }))),
        selected: (jsx(Item, Object.assign({ ref: itemRef, themeColors: colors.lightOrange }, { children: children }))),
    };
    return items[type];
});
const appendAnimation = keyframes `
  0% {
    max-height: 0;
    transform: scaleX(0);
  }
  50% {
    max-height: 4rem;
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
`;
const Item = styled.li `
  font-size: 0.8rem;
  color: ${({ themeColors }) => themeColors.fontColor};
  background-color: ${({ themeColors }) => themeColors.backgroundColor};
  border-radius: 0.25rem;
  border-bottom: 0.125rem solid ${({ themeColors }) => themeColors.borderColor};
  padding: 0.5rem;
`;
const NewItem = styled(Item) `
  transform-origin: left;
  animation: ${appendAnimation} 0.6s linear;
`;
const SelectableItem = styled(Item) `
  cursor: pointer;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 0 1px 2px 1px;
  }
`;

const NamedItem = styled.div `
  display: flex;
  flex-direction: column;

  label,
  span {
    font-size: 0.8rem;
    font-weight: 500;
    color: #7b7d8c;
  }
`;

const TabNavigation = ({ items, size, selectedIndex, onClick, }) => {
    const handleClick = (index) => () => {
        onClick && onClick(index);
    };
    const sizes = {
        medium: {
            fontSize: '1rem',
            padding: '0.25rem 0.5rem',
        },
        small: {
            fontSize: '0.8rem',
            padding: '0.125rem 0.25rem',
        },
    };
    return (jsx("nav", { children: jsx(TabList, Object.assign({ size: sizes[size] }, { children: items.map((item, index) => (jsx(TabListItem, Object.assign({ selected: index === selectedIndex, onClick: handleClick(index) }, { children: item.content }), index))) })) }));
};
const TabList = styled.ul `
  display: flex;
  list-style: none;
  font-size: ${({ size }) => size.fontSize};
  font-weight: 500;
  padding: 0;

  li {
    border: 0.0625rem solid #fc8e49;
    border-right-style: none;
    padding: ${({ size }) => size.padding};
    cursor: pointer;

    &:hover {
      color: #fc8e49;
    }

    &:first-child {
      border-bottom-left-radius: 0.25rem;
      border-top-left-radius: 0.25rem;
    }

    &:last-child {
      border-right-style: solid;
      border-bottom-right-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
    }
  }
`;
const TabListItem = styled.li `
  background-color: ${({ selected }) => (selected ? '#fff2eb' : 'none')};
  color: ${({ selected }) => (selected ? '#fc8e49' : '#7b7d8c')};
  font-weight: ${({ selected }) => (selected ? '500' : '400')};
`;

const TabContent = ({ contents, size, defaultIndex = 0 }) => {
    const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
    const handleClick = (index) => setSelectedIndex(index);
    return (jsxs(TabWrapper, { children: [jsx(TabNavigation, { items: contents.map(tab => tab.item), size: size, selectedIndex: selectedIndex, onClick: handleClick }), contents[selectedIndex].component] }));
};
const TabWrapper = styled.div `
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Tag = ({ text, color, size = 'standard' }) => {
    const themes = {
        blue: {
            backgroundColor: '#3a58de',
        },
        green: {
            backgroundColor: '#42c67f',
        },
        orange: {
            backgroundColor: '#fda772',
        },
        red: {
            backgroundColor: '#f4736b',
        },
    };
    const sizes = {
        large: {
            fontSize: '1.2rem',
            borderRadius: '16px',
            padding: '0.375rem 1rem',
        },
        small: {
            fontSize: '0.75rem',
            borderRadius: '12px',
            padding: '0.125rem 0.5rem',
        },
        standard: {
            fontSize: '1rem',
            borderRadius: '12px',
            padding: '0.25rem 0.75rem',
        },
    };
    return (jsx(TagContainer, Object.assign({ size: sizes[size], theme: themes[color] }, { children: text })));
};
const TagContainer = styled.span `
  color: #fff;
  font-size: ${({ size }) => size.fontSize};
  font-weight: 300;
  line-height: 1;
  padding: ${({ size }) => size.padding};
  background-color: ${({ theme }) => theme.backgroundColor};
  border-radius: ${({ size }) => size.borderRadius};
  user-select: none;
`;

export { Button$1 as Button, ButtonsWrapper, ContextMenu, CopyText, EditableContent, GearSpinner, GridTable, GridTableCell, Icon, IconButton, ListItem, NamedItem, Section, TabContent, Tag, ValueSelector };
